import Page from "../Components/Page";
import ButtonIcon from "../Components/Buttons/ButtonIcon";
import EditFieldMenuButton from "../Components/EditFieldMenuButton";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import CropYearTable from "../Components/Tables/CropYearTable.jsx";
import { useNavigate, useParams } from "react-router-dom";

import { useState } from "react";
import { useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  buildLegacyHeaders,
  fetchFarm,
  fetchField,
  fetchFieldSoilEnrichment,
} from "../Components/axiosFetchers";
import { useAuth } from "../Components/Auth/useAuth";
import { axiosInstance } from "../Components/axiosFetchers";
import { GrowerTableSurface } from "../Components/GrowerLayout.jsx";
import {
  ResourceBreadcrumb,
  buildFieldBreadcrumb,
} from "../Components/ResourceBreadcrumb.jsx";
import AggregatorSelectedGrowerPanel, {
  AggregatorScopeWarning,
  useSelectedAggregatorGrower,
} from "../Components/AggregatorSelectedGrowerPanel.jsx";
import {
  DATA_READ_SCOPE,
  DATA_WRITE_SCOPE,
  selectedGrowerHasScope,
} from "../portal/aggregatorGrowerContext.js";
import FieldSoilEstimatePanel from "../Components/FieldSoilEstimatePanel.jsx";
import FieldBoundaryPreview from "../Components/FieldBoundaryPreview.jsx";
import { buildFieldContextQuickFacts } from "../utils/resourceQuickFacts.js";
import { formatAcres } from "../utils/acreageFormatting.js";

const ACI_MIN_YEAR = 2009;
const defaultEndYear = Math.max(ACI_MIN_YEAR, new Date().getFullYear() - 2);
const defaultStartYear = Math.max(ACI_MIN_YEAR, defaultEndYear - 4);

function cropLabel(value) {
  if (!value) return "";
  return String(value)
    .split("_")
    .map((token) => token.charAt(0) + token.slice(1).toLowerCase())
    .join(" ");
}

function formatPct(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${Math.round(numeric * 100)}%` : "-";
}

function getApiError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return fallback;
}

function CropInventoryDialog({
  farmId,
  fieldId,
  onClose,
  onCreated,
  open,
  user,
}) {
  const [startYear, setStartYear] = useState(defaultStartYear);
  const [endYear, setEndYear] = useState(defaultEndYear);
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");

  function requestPayload() {
    return {
      start_year: Number(startYear),
      end_year: Number(endYear),
    };
  }

  async function handlePreview() {
    setError("");
    setIsPreviewing(true);
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/farms/${farmId}/fields/${fieldId}/cropyears/crop-inventory-preview`,
        requestPayload(),
        { headers: buildLegacyHeaders(user) }
      );
      setPreview(response.data);
    } catch (err) {
      setPreview(null);
      setError(getApiError(err, "Crop inventory preview could not be generated."));
    } finally {
      setIsPreviewing(false);
    }
  }

  async function handleCreate() {
    setError("");
    setIsCreating(true);
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/farms/${farmId}/fields/${fieldId}/cropyears/crop-inventory-create`,
        requestPayload(),
        { headers: buildLegacyHeaders(user) }
      );
      await onCreated(response.data);
      onClose();
    } catch (err) {
      setError(getApiError(err, "Draft crop years could not be created."));
    } finally {
      setIsCreating(false);
    }
  }

  const hasProposals = Boolean(preview?.proposed_cropyears?.length);
  const busy = isPreviewing || isCreating;

  return (
    <Dialog fullWidth maxWidth="md" onClose={busy ? undefined : onClose} open={open}>
      <DialogTitle>Create Draft Crop Years</DialogTitle>
      <DialogContent dividers>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              label="Start year"
              inputProps={{ min: ACI_MIN_YEAR, step: 1 }}
              onChange={(event) => {
                setStartYear(event.target.value);
                setPreview(null);
              }}
              type="number"
              value={startYear}
            />
            <TextField
              label="End year"
              inputProps={{ min: ACI_MIN_YEAR, step: 1 }}
              onChange={(event) => {
                setEndYear(event.target.value);
                setPreview(null);
              }}
              type="number"
              value={endYear}
            />
          </div>
          {busy ? <LinearProgress /> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {preview ? (
            <div className="space-y-4">
              <div className="rounded-md border border-slate-200">
                <div className="grid grid-cols-5 gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold uppercase text-slate-500">
                  <span>Year</span>
                  <span>Detected</span>
                  <span>Dominance</span>
                  <span>Confidence</span>
                  <span>Status</span>
                </div>
                {preview.results.map((result) => (
                  <div
                    className="grid grid-cols-5 gap-3 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0"
                    key={result.year}
                  >
                    <span className="font-semibold text-slate-900">{result.year}</span>
                    <span>
                      {cropLabel(result.mapped_crop) ||
                        result.dominant_label ||
                        "Not mapped"}
                    </span>
                    <span>{formatPct(result.dominant_pct)}</span>
                    <span>{result.confidence}</span>
                    <span>{result.status === "ok" ? "Available" : "Unavailable"}</span>
                  </div>
                ))}
              </div>
              {hasProposals ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
                  {preview.proposed_cropyears.length} draft crop year
                  {preview.proposed_cropyears.length === 1 ? "" : "s"} ready to create.
                </div>
              ) : (
                <Alert severity="info">No new mapped crop years are available for this range.</Alert>
              )}
              {preview.skipped_existing_years.length ? (
                <p className="text-xs font-semibold text-slate-500">
                  Existing years skipped: {preview.skipped_existing_years.join(", ")}
                </p>
              ) : null}
              {preview.skipped_unmapped_years.length ? (
                <p className="text-xs font-semibold text-slate-500">
                  Unmapped or unavailable years skipped: {preview.skipped_unmapped_years.join(", ")}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={busy} onClick={handlePreview} variant="outlined">
          Preview
        </Button>
        <Button disabled={busy || !hasProposals} onClick={handleCreate} variant="contained">
          Create Drafts
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function FieldPage({ requireSelectedGrower = false }) {
  let { farmId, fieldId } = useParams();
  const [field, setField] = useState(null);
  const [farm, setFarm] = useState(null);
  const [fieldCropYears, setFieldCropYears] = useState([]);
  const [soilEnrichmentState, setSoilEnrichmentState] = useState(null);
  const [cropInventoryOpen, setCropInventoryOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const selectedAggregatorGrower = useSelectedAggregatorGrower();
  const selectedAggregatorGrowerPublicId =
    selectedAggregatorGrower?.grower_profile_public_id || "";
  const [isLoading, setIsLoading] = useState(true);
  const canReadSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_READ_SCOPE);
  const canWriteSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_WRITE_SCOPE);
  const canUseSelectedGrowerWorkflow =
    !requireSelectedGrower ||
    (Boolean(selectedAggregatorGrowerPublicId) && canReadSelectedGrower);

  document.title = "Field Page - Field To Market Canada";

  async function refreshCropYears() {
    const response = await axiosInstance.get(
      process.env.REACT_APP_API_URL +
        "/farms/" +
        farmId +
        "/fields/" +
        fieldId +
        "/cropyears",
      { headers: buildLegacyHeaders(user) }
    );
    setFieldCropYears(response.data);
  }

  useEffect(() => {
    if (requireSelectedGrower && !canUseSelectedGrowerWorkflow) {
      setFarm(null);
      setField(null);
      setFieldCropYears([]);
      setSoilEnrichmentState(null);
      setIsLoading(false);
      return;
    }

    async function startupPage() {
      const [nextFarm, nextField, cropyearsResponse, nextSoilEnrichment] =
        await Promise.all([
          fetchFarm(farmId, user),
          fetchField(farmId, fieldId, user),
          axiosInstance.get(
            process.env.REACT_APP_API_URL +
              "/farms/" +
              farmId +
              "/fields/" +
              fieldId +
              "/cropyears",
            { headers: buildLegacyHeaders(user) }
          ),
          fetchFieldSoilEnrichment(farmId, fieldId, user).catch(() => null),
        ]);

      setFarm(nextFarm);
      setField(nextField);
      setFieldCropYears(cropyearsResponse.data);
      setSoilEnrichmentState(nextSoilEnrichment);
      setIsLoading(false);
    }

    startupPage();
  }, [
    canUseSelectedGrowerWorkflow,
    farmId,
    fieldId,
    requireSelectedGrower,
    selectedAggregatorGrowerPublicId,
    user,
  ]);

  const canCreateFromHistory = Boolean(
    field?.has_boundaries &&
      Array.isArray(field?.boundaries) &&
      field.boundaries.length >= 3
  );

  return (
    <Page
      title={field?.name || "Field"}
      showQuickFacts={true}
      quickFactsVariant="inline-card"
      headerBorderColor="border-[#34a853]"
      headerVariant="hybrid"
      contentWidth="wide"
      padding="px-4 pb-6 pt-5 md:px-6 md:pb-8"
      introContent={
        <p>
          {farm?.name || "Farm"}
          {field?.field_size
            ? ` / ${formatAcres(field.field_size)} acres`
            : ""}
        </p>
      }
      headerActions={
        canUseSelectedGrowerWorkflow && canWriteSelectedGrower ? (
          <EditFieldMenuButton text={"Edit Field"} fieldName={field?.name} />
        ) : null
      }
      quickFactsData={buildFieldContextQuickFacts({ farm, field })}
    >
      <div className="flex h-full w-full flex-col gap-6 text-[#666666]">
        {requireSelectedGrower ? (
          <AggregatorSelectedGrowerPanel
            selectedGrower={selectedAggregatorGrower}
          />
        ) : null}
        {requireSelectedGrower &&
        selectedAggregatorGrowerPublicId &&
        !canReadSelectedGrower ? (
          <AggregatorScopeWarning
            message="This selected grower link cannot view field records."
            requiredScope={DATA_READ_SCOPE}
          />
        ) : null}
        {!canUseSelectedGrowerWorkflow ? null : (
          <>
            <ResourceBreadcrumb
              ariaLabel="Field location"
              items={buildFieldBreadcrumb({
                farm,
                farmId,
                field,
                fieldId,
              })}
            />
            {field ? (
              <FieldBoundaryPreview
                field={field}
                editTo={canWriteSelectedGrower ? `/farm/${farmId}/field/${fieldId}/form` : undefined}
              />
            ) : null}
            {soilEnrichmentState ? (
              <FieldSoilEstimatePanel
                enrichment={soilEnrichmentState.enrichment}
                reason={soilEnrichmentState.reason}
              />
            ) : null}
            <div className="flex flex-col justify-between gap-3 border-y border-slate-200 bg-[#fcfcfa] px-4 py-3 sm:flex-row sm:items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Field workflow</p>
                <h2 className="mt-1 text-[22px] font-black text-slate-900">Crop years and analyses</h2>
              </div>
              {canWriteSelectedGrower ? (
              <div className="flex shrink-0 flex-wrap gap-2">
                <Tooltip title={canCreateFromHistory ? "Create draft crop years from AAFC crop history" : "Save a field boundary first"}>
                  <span>
                    <ButtonIcon
                      text={"Create From History"}
                      onClick={() => setCropInventoryOpen(true)}
                      grow={false}
                      disabled={!canCreateFromHistory}
                      Icon={TravelExploreIcon}
                    />
                  </span>
                </Tooltip>
                <ButtonIcon
                  text={"Add New Crop Year"}
                  onClick={() =>
                    navigate(`/farm/${farmId}/field/${fieldId}/cropyear`)
                  }
                  grow={false}
                  disabled={false}
                  Icon={AddCircleOutlineIcon}
                />
              </div>
              ) : null}
            </div>

            <GrowerTableSurface
              className="w-full"
              meta={`${fieldCropYears.length} crop year${
                fieldCropYears.length === 1 ? "" : "s"
              } in this field`}
              title="Crop Years"
            >
              <div className="w-full overflow-x-auto">
                <CropYearTable
                  canWrite={canWriteSelectedGrower}
                  fieldCropYears={fieldCropYears}
                  setFieldCropYears={setFieldCropYears}
                  isLoading={isLoading}
                />
              </div>
            </GrowerTableSurface>
            <p className="px-1 text-xs leading-5 text-slate-500">
              Deleting a crop year also deletes its analyses and project associations. For double cropping, use the primary crop because secondary-crop production is not currently included in Fieldprint Analysis.
            </p>
            <CropInventoryDialog
              farmId={farmId}
              fieldId={fieldId}
              onClose={() => setCropInventoryOpen(false)}
              onCreated={refreshCropYears}
              open={cropInventoryOpen}
              user={user}
            />
          </>
        )}
      </div>
    </Page>
  );
}
